import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getErrorMessage } from "../../api/errorMessage";
import {
  deleteContactMessage,
  deleteContactMessageNote,
  getContactMessage,
  listContactMessages,
  patchContactMessage,
  postContactMessageEmail,
  postContactMessageEmailAnalysisRun,
  postContactMessageNote,
  postContactMessageReply,
  publicCreateContactMessage,
} from "../../api/services/contactMessagesApi";
import { isAdminEmailMockTransportEnabled } from "../../config/adminEmailMock";
import {
  mergeEmailMessagesIntoDetail,
  patchEmailMessageClientAnalysis,
  upsertEmailMessageInList,
} from "../../utils/adminDetailEmailMessages";
import {
  buildMockInboundFollowUp,
  buildMockInboundReply,
  buildMockOutboundEmail,
} from "../../utils/adminEmailAnalysisMock";
import { pushToast } from "./toastSlice";

function snapshotContactRow(row) {
  if (!row) return null;
  return {
    id: row.id,
    status: row.status,
    seenAt: row.seenAt,
    unread: row.unread,
    processing: row.processing,
  };
}

export const fetchContactMessagesList = createAsyncThunk(
  "contactMessages/fetchList",
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await listContactMessages(params);
      return { data, params };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Could not load contact messages",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const fetchContactMessageById = createAsyncThunk(
  "contactMessages/fetchById",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getContactMessage(id);
      return data;
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Could not load message",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const updateContactMessage = createAsyncThunk(
  "contactMessages/patch",
  async ({ id, body }, { dispatch, getState, rejectWithValue }) => {
    try {
      const st = getState();
      const prevRow =
        st.contactMessages.detail?.id === id
          ? st.contactMessages.detail
          : st.contactMessages.list.items.find((r) => r.id === id) || null;
      const prev = snapshotContactRow(prevRow);
      const { data } = await patchContactMessage(id, body);
      dispatch(
        pushToast({
          variant: "success",
          title: "Saved",
          message: "Contact message updated.",
        })
      );
      return { id, data, prev };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Update failed",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const bulkPatchContactMessages = createAsyncThunk(
  "contactMessages/bulkPatch",
  async ({ ids, body }, { dispatch, rejectWithValue }) => {
    const ok = [];
    const fail = [];
    for (const id of ids) {
      try {
        const { data } = await patchContactMessage(id, body);
        ok.push({ id, data });
      } catch (e) {
        fail.push({ id, error: getErrorMessage(e) });
      }
    }
    if (ok.length && !fail.length) {
      dispatch(
        pushToast({
          variant: "success",
          title: "Updated",
          message:
            ok.length === 1
              ? "1 message updated."
              : `${ok.length} messages updated.`,
        })
      );
    } else if (ok.length && fail.length) {
      dispatch(
        pushToast({
          variant: "info",
          title: "Partially updated",
          message: `${ok.length} succeeded, ${fail.length} failed.`,
        })
      );
    } else if (fail.length) {
      dispatch(
        pushToast({
          variant: "error",
          title: "Bulk update failed",
          message: fail[0]?.error || "Could not update messages.",
        })
      );
      return rejectWithValue(fail);
    }
    return { ok, fail };
  }
);

export const bulkRemoveContactMessages = createAsyncThunk(
  "contactMessages/bulkDelete",
  async (ids, { dispatch, rejectWithValue }) => {
    const ok = [];
    const fail = [];
    for (const id of ids) {
      try {
        await deleteContactMessage(id);
        ok.push(id);
      } catch (e) {
        fail.push({ id, error: getErrorMessage(e) });
      }
    }
    if (ok.length && !fail.length) {
      dispatch(
        pushToast({
          variant: "success",
          title: "Deleted",
          message:
            ok.length === 1
              ? "1 message removed."
              : `${ok.length} messages removed.`,
        })
      );
    } else if (ok.length && fail.length) {
      dispatch(
        pushToast({
          variant: "info",
          title: "Partially deleted",
          message: `${ok.length} removed, ${fail.length} failed.`,
        })
      );
    } else if (fail.length) {
      dispatch(
        pushToast({
          variant: "error",
          title: "Bulk delete failed",
          message: fail[0]?.error || "Could not delete messages.",
        })
      );
      return rejectWithValue(fail);
    }
    return { ok, fail };
  }
);

export const removeContactMessage = createAsyncThunk(
  "contactMessages/delete",
  async (id, { dispatch, getState, rejectWithValue }) => {
    try {
      const st = getState();
      const rowEntity =
        st.contactMessages.detail?.id === id
          ? st.contactMessages.detail
          : st.contactMessages.list.items.find((r) => r.id === id) || null;
      const row = snapshotContactRow(rowEntity);
      await deleteContactMessage(id);
      dispatch(
        pushToast({
          variant: "success",
          title: "Deleted",
          message: "Contact message removed.",
        })
      );
      return { id, row };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Delete failed",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

const APPEND_INBOUND_EMAIL_CONTACT = "contactMessages/appendInboundEmailToContactMessageDetail";

export const sendContactMessageEmail = createAsyncThunk(
  "contactMessages/sendEmail",
  async ({ id, subject, body }, { dispatch, getState, rejectWithValue }) => {
    const detail = getState().contactMessages?.detail;
    const clientEmail = detail?.email ?? "";
    try {
      if (isAdminEmailMockTransportEnabled()) {
        await new Promise((r) => setTimeout(r, 480));
        const message = buildMockOutboundEmail({
          clientEmail,
          subject,
          body,
        });
        dispatch(
          pushToast({
            variant: "success",
            title: "Email sent (mock)",
            message: "Demo transport: simulated client replies will append to the thread.",
          })
        );
        const inbound = buildMockInboundReply({ clientEmail, subject, variant: "primary" });
        window.setTimeout(() => {
          dispatch({ type: APPEND_INBOUND_EMAIL_CONTACT, payload: { id, message: inbound } });
        }, 2000);
        const followUp = buildMockInboundFollowUp({ clientEmail, subject });
        window.setTimeout(() => {
          dispatch({ type: APPEND_INBOUND_EMAIL_CONTACT, payload: { id, message: followUp } });
        }, 4200);
        return { id, message };
      }

      const { data } = await postContactMessageEmail(id, { subject, body });
      const jobId = data?.jobId ?? null;
      const message = data?.item ?? data?.message ?? null;
      const hasMessage = message && typeof message === "object" && message.id != null;
      if (hasMessage) {
        dispatch(
          pushToast({
            variant: "success",
            title: "Email sent",
            message: "Your message was delivered to the thread.",
          })
        );
      } else if (jobId) {
        dispatch(
          pushToast({
            variant: "info",
            title: "Sending…",
            message: data?.message || "Queued for delivery — the thread updates when send completes.",
          })
        );
      } else {
        dispatch(
          pushToast({
            variant: "info",
            title: "Email queued",
            message: "Your send was accepted.",
          })
        );
      }
      return { id, jobId, message: hasMessage ? message : null };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Email not sent",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const rerunContactMessageClientEmailAnalysis = createAsyncThunk(
  "contactMessages/rerunClientEmailAnalysis",
  async ({ id, emailMessageId }, { dispatch, rejectWithValue }) => {
    try {
      await postContactMessageEmailAnalysisRun(id, emailMessageId, {});
      dispatch(
        pushToast({
          variant: "success",
          title: "Analysis queued",
          message: "This message will update when the model finishes.",
        })
      );
      await dispatch(fetchContactMessageById(id));
      return { id, emailMessageId };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Re-analyse failed",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const sendContactMessageReply = createAsyncThunk(
  "contactMessages/reply",
  async ({ id, body, internal }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await postContactMessageReply(id, {
        body,
        internal: Boolean(internal),
      });
      dispatch(
        pushToast({
          variant: "success",
          title: "Reply sent",
        })
      );
      return { id, reply: data };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Reply failed",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const addContactMessageNote = createAsyncThunk(
  "contactMessages/addNote",
  async ({ id, body }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await postContactMessageNote(id, { body });
      dispatch(
        pushToast({
          variant: "success",
          title: "Note added",
        })
      );
      return { id, note: data };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Could not add note",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const removeContactMessageNote = createAsyncThunk(
  "contactMessages/deleteNote",
  async ({ id, noteId }, { dispatch, rejectWithValue }) => {
    try {
      await deleteContactMessageNote(id, noteId);
      dispatch(
        pushToast({
          variant: "success",
          title: "Note removed",
        })
      );
      return { id, noteId };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Could not delete note",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const submitPublicContactMessage = createAsyncThunk(
  "contactMessages/publicSubmit",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await publicCreateContactMessage(payload);
      dispatch(
        pushToast({
          variant: "success",
          title: "Message sent",
          message: "Thanks — we will be in touch.",
        })
      );
      return data;
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Could not send message",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

const initialState = {
  lastListParams: {},
  list: { items: [], total: 0, page: 1, limit: 50 },
  listStatus: "idle",
  listError: null,
  detail: null,
  detailStatus: "idle",
  detailError: null,
};

function normalizeListPayload(payload) {
  const raw = payload?.data ?? payload;
  const items = Array.isArray(raw?.items) ? raw.items : Array.isArray(raw) ? raw : [];
  const total =
    typeof raw?.total === "number" ? raw.total : items.length;
  const page = typeof raw?.page === "number" ? raw.page : 1;
  const limit = typeof raw?.limit === "number" ? raw.limit : items.length || 50;
  return { items, total, page, limit };
}

const contactMessagesSlice = createSlice({
  name: "contactMessages",
  initialState,
  reducers: {
    clearContactMessageDetail(state) {
      state.detail = null;
      state.detailStatus = "idle";
      state.detailError = null;
      state.clientEmailOutbound = null;
      state.clientEmailSendHttpPending = false;
    },
    mergeContactMessageFromSocket(state, action) {
      const patch = action.payload;
      if (!patch?.id) return;
      if (state.detail && state.detail.id === patch.id) {
        state.detail = { ...state.detail, ...patch };
      }
      state.list.items = state.list.items.map((row) =>
        row.id === patch.id ? { ...row, ...patch } : row
      );
    },
    applyProcessingUpdateFromSocket(state, action) {
      const { id, processing } = action.payload || {};
      if (!id) return;
      if (state.detail && state.detail.id === id) {
        state.detail = { ...state.detail, processing };
      }
      state.list.items = state.list.items.map((row) =>
        row.id === id ? { ...row, processing } : row
      );
    },
    appendInboundEmailToContactMessageDetail(state, action) {
      const { id, message } = action.payload || {};
      if (!message || !state.detail || state.detail.id !== id) return;
      const prev = Array.isArray(state.detail.emailMessages) ? state.detail.emailMessages : [];
      state.detail = {
        ...state.detail,
        emailMessages: [...prev, message],
      };
    },
    mergeContactMessageEmailMessage(state, action) {
      const { id, message } = action.payload || {};
      if (!message || !state.detail || String(state.detail.id) !== String(id)) return;
      const prev = state.detail.emailMessages || [];
      state.detail = {
        ...state.detail,
        emailMessages: upsertEmailMessageInList(prev, message),
      };
      const dir = String(message.direction || "").toLowerCase();
      const isOut = dir === "outbound" || dir === "out";
      if (
        isOut &&
        state.clientEmailOutbound &&
        String(state.clientEmailOutbound.parentId) === String(id)
      ) {
        state.clientEmailOutbound = null;
      }
    },
    applyContactMessageEmailAnalysisFromSocket(state, action) {
      const { id, emailMessageId, clientAnalysis } = action.payload || {};
      if (!id || emailMessageId == null || !state.detail || String(state.detail.id) !== String(id)) {
        return;
      }
      const prev = state.detail.emailMessages || [];
      state.detail = {
        ...state.detail,
        emailMessages: patchEmailMessageClientAnalysis(prev, emailMessageId, clientAnalysis),
      };
    },
    clearContactMessageClientEmailOutbound(state, action) {
      const { parentId, jobId } = action.payload || {};
      const cur = state.clientEmailOutbound;
      if (!cur) return;
      if (parentId != null && String(cur.parentId) !== String(parentId)) return;
      if (jobId != null && String(cur.jobId) !== String(jobId)) return;
      state.clientEmailOutbound = null;
    },
    applyContactMessageClientEmailJobUpdated(state, action) {
      const p = action.payload || {};
      const typ = String(p.type || "").toLowerCase();
      if (typ !== "send_client_email") return;
      const status = String(p.jobStatus || "").toLowerCase();
      if (status !== "failed") return;
      const cur = state.clientEmailOutbound;
      if (!cur || String(cur.jobId) !== String(p.jobId)) return;
      if (!state.detail || String(state.detail.id) !== String(p.resourceId)) return;
      state.clientEmailOutbound = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContactMessagesList.pending, (state) => {
        state.listStatus = "loading";
        state.listError = null;
      })
      .addCase(fetchContactMessagesList.fulfilled, (state, action) => {
        state.listStatus = "succeeded";
        state.lastListParams = action.payload.params || {};
        state.list = normalizeListPayload(action.payload.data);
        state.listError = null;
      })
      .addCase(fetchContactMessagesList.rejected, (state, action) => {
        state.listStatus = "failed";
        state.listError = action.payload || action.error.message;
      })
      .addCase(fetchContactMessageById.pending, (state) => {
        state.detailStatus = "loading";
        state.detailError = null;
      })
      .addCase(fetchContactMessageById.fulfilled, (state, action) => {
        state.detailStatus = "succeeded";
        state.detail = mergeEmailMessagesIntoDetail(action.payload);
        state.detailError = null;
      })
      .addCase(fetchContactMessageById.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.detail = null;
        state.detailError = action.payload || action.error.message;
      })
      .addCase(updateContactMessage.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        if (state.detail && state.detail.id === id) {
          state.detail = { ...state.detail, ...data };
        }
        state.list.items = state.list.items.map((row) =>
          row.id === id ? { ...row, ...data } : row
        );
      })
      .addCase(removeContactMessage.fulfilled, (state, action) => {
        const { id } = action.payload;
        state.list.items = state.list.items.filter((r) => r.id !== id);
        state.list.total = Math.max(0, (state.list.total || 1) - 1);
        if (state.detail && state.detail.id === id) {
          state.detail = null;
          state.detailStatus = "idle";
        }
      })
      .addCase(bulkPatchContactMessages.fulfilled, (state, action) => {
        const { ok } = action.payload || {};
        if (!Array.isArray(ok)) return;
        for (const { id, data } of ok) {
          if (state.detail && state.detail.id === id) {
            state.detail = { ...state.detail, ...data };
          }
          state.list.items = state.list.items.map((row) =>
            row.id === id ? { ...row, ...data } : row
          );
        }
      })
      .addCase(bulkRemoveContactMessages.fulfilled, (state, action) => {
        const removed = new Set(action.payload?.ok || []);
        if (!removed.size) return;
        state.list.items = state.list.items.filter((r) => !removed.has(r.id));
        state.list.total = Math.max(0, (state.list.total || removed.size) - removed.size);
        if (state.detail && removed.has(state.detail.id)) {
          state.detail = null;
          state.detailStatus = "idle";
        }
      })
      .addCase(sendContactMessageReply.fulfilled, (state, action) => {
        const { id, reply } = action.payload;
        if (state.detail && state.detail.id === id) {
          const replies = Array.isArray(state.detail.replies)
            ? [...state.detail.replies, reply]
            : [reply];
          state.detail = { ...state.detail, replies };
        }
      })
      .addCase(sendContactMessageEmail.pending, (state) => {
        state.clientEmailSendHttpPending = true;
      })
      .addCase(sendContactMessageEmail.fulfilled, (state, action) => {
        state.clientEmailSendHttpPending = false;
        const { id, jobId, message } = action.payload || {};
        if (!id || !state.detail || String(state.detail.id) !== String(id)) return;
        if (message) {
          const prev = state.detail.emailMessages || [];
          state.detail = {
            ...state.detail,
            emailMessages: upsertEmailMessageInList(prev, message),
          };
          state.clientEmailOutbound = null;
        } else if (jobId) {
          state.clientEmailOutbound = { parentId: id, jobId };
        }
      })
      .addCase(sendContactMessageEmail.rejected, (state) => {
        state.clientEmailSendHttpPending = false;
      })
      .addCase(addContactMessageNote.fulfilled, (state, action) => {
        const { id, note } = action.payload;
        if (state.detail && state.detail.id === id) {
          const notes = Array.isArray(state.detail.notes)
            ? [...state.detail.notes, note]
            : [note];
          state.detail = { ...state.detail, notes };
        }
      })
      .addCase(removeContactMessageNote.fulfilled, (state, action) => {
        const { id, noteId } = action.payload;
        if (state.detail && state.detail.id === id) {
          const notes = (state.detail.notes || []).filter((n) => n.id !== noteId);
          state.detail = { ...state.detail, notes };
        }
      });
  },
});

export const {
  clearContactMessageDetail,
  mergeContactMessageFromSocket,
  applyProcessingUpdateFromSocket,
  appendInboundEmailToContactMessageDetail,
  mergeContactMessageEmailMessage,
  applyContactMessageEmailAnalysisFromSocket,
  clearContactMessageClientEmailOutbound,
  applyContactMessageClientEmailJobUpdated,
} = contactMessagesSlice.actions;

export default contactMessagesSlice.reducer;
